import * as React from 'react';
import { useEffect, useRef } from 'react';

import { Box, HStack } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { AccessibilityInfo, findNodeHandle } from 'react-native';

import { A11yWrapper } from 'components/a11y-wrapper';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useRoute } from 'hooks/navigation/use-route';
import { primitive } from 'styles/constants/primitives';
import { isNativeIOS } from 'utils/environment';
import { routes } from 'utils/routing';

import { StoreOrderButton } from '..';

import { StoreCardActions, StoreCardDetails } from './components';
import { Container, Distance } from './store-card.styled';
import { StoreCardViewProps } from './types';

export const StoreCardView: React.FC<StoreCardViewProps> = ({
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardPress,
  onDiagnosticsClick,
  onFavoriteClick,
  onOrderPress,
  onInfoPress,
  storeDetails,
  errorMessage,
  isInMapView,
  isInSupportForm,
  isSelected,
  isLastItem,
}) => {
  const { formatMessage } = useIntl();
  const {
    address,
    distanceText,
    restaurant,
    restaurantStatus: { canBeSelected, text: storeStatusText },
    isHalal,
    city,
    accessInstructions,
  } = storeDetails;
  const { pathname } = useRoute();
  const isFavoriteSearch = pathname === routes.signUp;
  const halalTitle = isHalal ? formatMessage({ id: 'isHalalTitle' }) : '';
  const accessibilityLabel = `${address} ${city} ${storeStatusText}, ${halalTitle}, ${accessInstructions}`;
  const elementFocusAfterExpandRef = useRef(null);

  // This useEffect handles focus management for accessibility. It focuses the
  // first child element when the parent component becomes selected.
  useEffect(() => {
    if (isSelected && elementFocusAfterExpandRef.current) {
      const nodeHandle = findNodeHandle(elementFocusAfterExpandRef.current);
      if (nodeHandle) {
        return AccessibilityInfo.setAccessibilityFocus(nodeHandle);
      }
    }
  }, [isSelected]);

  const handleOnPress = () => {
    if (!isSelected) {
      onCardPress?.();
    }
  };

  return (
    <Container
      onPress={handleOnPress}
      accessible={!isSelected}
      accessibilityState={{ expanded: !!isSelected }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      dd-action-name={`${storeDetails.address} open accordion`}
      aria-expanded={isSelected}
      backgroundColor={!canBeSelected ? primitive.$blackOpacity4 : undefined}
      paddingBottom={isNativeIOS() && isLastItem ? '$7' : undefined}
    >
      <HStack>
        <StoreCardDetails
          errorMessage={errorMessage}
          storeDetails={storeDetails}
          isInMapView={isInMapView}
          elementFocusAfterExpandRef={elementFocusAfterExpandRef}
          distanceComponent={
            <Box alignSelf={'flex-end'}>
              <A11yWrapper flexGrow={0}>
                <Distance>{distanceText}</Distance>
              </A11yWrapper>
            </Box>
          }
        />
      </HStack>
      <HStack>
        {(isSelected || isInMapView) && (
          <StoreCardActions
            dd-action-name={`${address} ${isFavorite ? 'unfavorite' : 'favorite'}`}
            isFavorite={isFavorite}
            canBeSelected={canBeSelected}
            onDiagnosticsClick={!isFavoriteSearch ? onDiagnosticsClick : undefined}
            onFavoritesClick={!isFavoriteSearch ? onFavoriteClick : onCardPress}
            onInfoPress={onInfoPress}
            isUpdatingFavorites={isUpdatingFavorites}
            containerStyle={{
              flex: onDiagnosticsClick ? 2 : 1,
              paddingTop: '$7',
              justifyContent: 'space-around',
              paddingRight: '$6',
            }}
          />
        )}
        <Box flex={5}>
          {isSelected && onInfoPress && !canBeSelected && (
            <ActionButton
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={onInfoPress}
              alignSelf={'center'}
              marginTop={'$5'}
              borderColor={primitive.bk.$bbqBrown}
              _text={{ color: primitive.bk.$bbqBrown }}
            >
              {formatMessage({ id: 'viewStoreHours' })}
            </ActionButton>
          )}
          {(isSelected || isInMapView) && canBeSelected && (
            <StoreOrderButton
              isInSupportForm={isInSupportForm}
              onOrderPress={onOrderPress}
              restaurant={restaurant}
              disabled={!canBeSelected}
            />
          )}
        </Box>
      </HStack>
    </Container>
  );
};
